import { motion } from 'framer-motion'
import {
  Globe, ShoppingBag, Smartphone, User,
  LayoutDashboard, Server, Mail, Wrench,
  ArrowRight, CheckCircle2, ExternalLink
} from 'lucide-react'

const services = [
  {
    icon: Globe,
    title: "Business Website",
    desc: "Professional site for your shop, cafe, or service business — built to load fast, rank on Google, and turn visitors into enquiries.",
    deliverables: [
      "Custom design tailored to your brand & industry",
      "Mobile-optimised, fast-loading (Core Web Vitals)",
      "Contact forms, Google Maps & social links",
      "Domain + hosting setup & configuration",
      "3 months post-launch support included",
    ],
    price: "From $1,200",
    tags: ["Design", "Development", "Hosting"],
    proof: null,
    popular: false,
  },
  {
    icon: Smartphone,
    title: "Digital Ordering App",
    desc: "Customers order from their phone, staff manage in real time — no app download needed. Already live at Majnu Bar.",
    deliverables: [
      "Customer ordering interface (any device, browser)",
      "Staff POS dashboard with real-time order flow",
      "Live menu updates & item availability control",
      "Order history and basic analytics",
      "Hosted, maintained and updated after launch",
    ],
    price: "From $2,500 + $99/mo",
    tags: ["Web App", "POS", "Real-time"],
    proof: { label: "Live at Majnu Shisha Bar & Lounge", url: "https://app.majnu.com.au/table/MAJNU001" },
    popular: true,
  },
  {
    icon: ShoppingBag,
    title: "E-commerce Store",
    desc: "Sell online with a full payment gateway, inventory management and automated order notifications — ready to scale.",
    deliverables: [
      "Product catalogue with categories & search",
      "Secure checkout (Stripe / PayPal)",
      "Inventory and stock management",
      "Order confirmation emails & SMS",
      "Admin dashboard to manage everything",
    ],
    price: "From $2,000",
    tags: ["Custom", "Stripe", "Payments"],
    proof: null,
    popular: false,
  },
  {
    icon: User,
    title: "Portfolio Website",
    desc: "A sharp, fast personal site that positions you as a professional and gets you hired, booked, or taken seriously.",
    deliverables: [
      "Custom design that reflects your personal brand",
      "Case studies and project showcases",
      "Contact form, resume download, social links",
      "Optimised for recruiter and client reading patterns",
      "Built with React — fast, modern, impressive",
    ],
    price: "From $800",
    tags: ["Design", "React", "Fast"],
    proof: { label: "This site — built for Akshay Rao", url: null },
    popular: false,
  },
  {
    icon: LayoutDashboard,
    title: "Custom Web App",
    desc: "Any workflow turned into a clean, automated tool — CRM, booking system, staff management, dashboards, anything custom.",
    deliverables: [
      "Full custom design and functionality",
      "User authentication and role-based access",
      "Database design, REST API, admin panel",
      "Multi-user, multi-store or multi-location ready",
      "Maintenance and support plan available",
    ],
    price: "Custom quote",
    tags: ["React", "FastAPI", "MongoDB"],
    proof: { label: "Building CRM + HRMS for Melbourne supermarket group", url: null },
    popular: false,
  },
  {
    icon: Server,
    title: "IT Store Setup",
    desc: "Full physical IT infrastructure for your shop — from security cameras to point of sale — delivered and live on day one.",
    deliverables: [
      "CCTV security camera system & monitoring",
      "Security alarm installation",
      "PA & background music system",
      "Structured data cabling throughout premises",
      "POS system installation & staff training",
    ],
    price: "From $1,500",
    tags: ["CCTV", "POS", "Cabling"],
    proof: { label: "Delivered for Sadar Bazaar — Punjab Imports", url: null },
    popular: false,
  },
  {
    icon: Mail,
    title: "Domain & Business Email",
    desc: "Get a professional domain and business email — set up correctly, secured, and working from day one.",
    deliverables: [
      "Domain registration (yourbusiness.com.au)",
      "Google Workspace or Microsoft 365 setup",
      "Professional email for your whole team",
      "Spam filtering, security & MX records",
      "Walkthrough on managing your inbox",
    ],
    price: "From $200/yr",
    tags: ["Domain", "Google", "Microsoft"],
    proof: null,
    popular: false,
  },
  {
    icon: Wrench,
    title: "Managed IT Support",
    desc: "Monthly IT support for your existing setup — proactive maintenance and priority response so you never stress about IT.",
    deliverables: [
      "Monthly proactive maintenance visit",
      "Priority support — 2-hour response guarantee",
      "Hardware lifecycle & warranty management",
      "Security patches and software updates",
      "Direct WhatsApp line to Akshay",
    ],
    price: "From $299/mo",
    tags: ["Support", "Maintenance", "Priority"],
    proof: { label: "Managing IT across 4 stores in Melbourne", url: null },
    popular: false,
  },
]

export default function Services({ onSelectService, onBookConsultation }) {
  return (
    <section id="services" className="py-16 md:py-24 bg-[#F3F4F6]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.5 }}
          className="flex items-center gap-3 mb-4">
          <span className="font-mono text-[#0047AB] text-xs tracking-[0.3em] uppercase">Services</span>
          <span className="h-px flex-1 max-w-[60px] bg-[#0047AB]/25" />
        </motion.div>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 md:mb-14">
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.05 }}
            className="font-sans font-bold text-4xl md:text-5xl tracking-tight text-[#111111]">
            One person.
            <br /><span className="text-[#6B7280]">End-to-end delivery.</span>
          </motion.h2>

          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
            viewport={{ once: true }} transition={{ delay: 0.1 }}
            className="font-body text-[#6B7280] text-sm md:text-base max-w-sm leading-relaxed md:text-right">
            From getting your domain to launching a full app — full accountability, no agency markup, no finger-pointing between vendors.
          </motion.p>
        </div>

        {/* Services grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {services.map((service, i) => (
            <ServiceCard key={service.title} service={service} index={i} onSelectService={onSelectService} />
          ))}
        </div>

        {/* Bottom note */}
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
          viewport={{ once: true }} transition={{ delay: 0.3 }}
          className="mt-8 p-5 border border-[#E5E7EB] rounded-2xl bg-white flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-1">
            <div className="font-sans font-semibold text-[#111111] text-sm mb-0.5">Not sure which service fits you?</div>
            <div className="font-body text-[#6B7280] text-sm">Use the AI assistant at the bottom right — ask "what do I need for my cafe?" and get an instant, honest answer.</div>
          </div>
          <button onClick={() => onBookConsultation ? onBookConsultation() : (onSelectService && onSelectService('general enquiry'))}
            className="shrink-0 flex items-center gap-2 px-5 py-2.5 bg-[#111111] text-white text-sm font-semibold rounded-xl hover:bg-[#0047AB] transition-colors duration-200">
            Book a call — $8.99 <ArrowRight size={13} />
          </button>
        </motion.div>
      </div>
    </section>
  )
}

function ServiceCard({ service, index, onSelectService }) {
  const Icon = service.icon
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      className="group flex flex-col bg-white border border-[#E5E7EB] rounded-2xl p-6 hover:border-[#0047AB]/25 hover:shadow-card-hover transition-all duration-300"
      data-testid={`service-detail-${index}`}
    >
      {/* Top row */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-[#EBF2FF] border border-[#0047AB]/12 rounded-xl group-hover:bg-[#0047AB] group-hover:border-[#0047AB] transition-all duration-300">
            <Icon size={15} className="text-[#0047AB] group-hover:text-white transition-colors duration-300" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-sans font-bold text-[#111111] text-base">{service.title}</h3>
              {service.popular && (
                <span className="px-2 py-0.5 bg-[#EBF2FF] text-[#0047AB] text-[9px] font-mono uppercase tracking-wider rounded-full border border-[#0047AB]/20">
                  Popular
                </span>
              )}
            </div>
            <div className="font-mono text-[#0047AB] text-xs mt-0.5">{service.price}</div>
          </div>
        </div>
      </div>

      {/* Description */}
      <p className="font-body text-[#6B7280] text-sm leading-relaxed mb-4">{service.desc}</p>

      {/* Deliverables */}
      <ul className="space-y-1.5 mb-4 flex-1">
        {service.deliverables.map((item) => (
          <li key={item} className="flex items-start gap-2">
            <CheckCircle2 size={12} className="text-[#0047AB] mt-0.5 shrink-0" />
            <span className="font-body text-[#374151] text-xs leading-relaxed">{item}</span>
          </li>
        ))}
      </ul>

      {/* Tags + proof */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {service.tags.map(tag => (
          <span key={tag} className="px-2 py-0.5 text-[9px] font-mono text-[#9CA3AF] border border-[#E5E7EB] rounded-md bg-[#F3F4F6]">
            {tag}
          </span>
        ))}
      </div>

      {service.proof && (
        <div className="flex items-center gap-2 mb-4 py-2.5 px-3 bg-[#FAFAFA] border border-[#E5E7EB] rounded-xl">
          <CheckCircle2 size={11} className="text-emerald-500 shrink-0" />
          {service.proof.url ? (
            <a href={service.proof.url} target="_blank" rel="noopener noreferrer"
              className="font-mono text-[10px] text-[#6B7280] hover:text-[#0047AB] transition-colors flex items-center gap-1">
              {service.proof.label} <ExternalLink size={9} />
            </a>
          ) : (
            <span className="font-mono text-[10px] text-[#6B7280]">{service.proof.label}</span>
          )}
        </div>
      )}

      {/* CTA */}
      <button
        onClick={() => onSelectService && onSelectService(service.title)}
        data-testid={`service-enquire-${index}`}
        className="w-full flex items-center justify-center gap-2 py-2.5 border border-[#E5E7EB] rounded-xl text-[#374151] text-sm font-semibold hover:border-[#0047AB] hover:text-[#0047AB] hover:bg-[#EBF2FF] transition-all duration-200"
      >
        Get a quote
        <ArrowRight size={13} />
      </button>
    </motion.div>
  )
}
